import React, { useEffect, useState } from 'react';
import ShopifyImage from '../../../assets/images/marketPlace/shopify.png';
import AmazonImage from '../../../assets/images/marketPlace/amazon.png';
import ShopifyForm from '../../../component/views/seller/marketPlace/shopifyForm';
import { selectIsNightMode } from '../../../redux/slices/modeSlice';
import { useSelector } from 'react-redux';
import { marketPlaceApiService } from '../../../service/seller/marketPlaceApiService';
import { ToastTypes, useMessage } from '../../../base/context/MessageProvider';
import { useLoader } from '../../../base/context/LoaderProvider';
import { Link, useNavigate } from 'react-router-dom';
import DeletePopUp from '../../../component/library/deletePopUp';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import './style.css';

// Green Tick SVG Component
const GreenTick = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="green-tick-icon">
    <circle cx="12" cy="12" r="10" fill="#28a745" />
    <path d="M9 12L11 14L15 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function MarketplaceApis() {
  const [shopifyModal, setShopifyModal] = useState(false);
  const [amazonModal, setAmazonModal] = useState(false);
  const [isShopifyConnected, setIsShopifyConnected] = useState(false);
  const [isAmazonConnected, setIsAmazonConnected] = useState(false);
  const [stores, setStores] = useState([]);
  const [amazonTableVisible, setAmazonTableVisible] = useState(true);
  const [shopifyTableVisible, setShopifyTableVisible] = useState(false);
  const [disconnectModal, setDisconnectModal] = useState(false);
  const [storeToDisconnect, setStoreToDisconnect] = useState(null);
  const [visibleFields, setVisibleFields] = useState({}); // Track visibility of sensitive fields per store

  const isNightMode = useSelector(selectIsNightMode);
  const message = useMessage();
  const loader = useLoader();
  const navigate = useNavigate();

  // Amazon video (ShopifyForm-like behavior)
  const videoId = 'fEgta2zjlx8';
  const [embedUrl, setEmbedUrl] = useState(`https://www.youtube.com/embed/${videoId}`);

  useEffect(() => {
    handleStores();
    
    // Check for OAuth success redirect
    const urlParams = new URLSearchParams(window.location.search);
    const shopifyConnected = urlParams.get('shopify_connected');
    const shop = urlParams.get('shop');
    
    if (shopifyConnected === 'true' && shop) {
      loader.showLoading(false); // Hide loading after successful connection
      message.showToast(`Shopify store "${shop}" connected successfully!`, ToastTypes.SUCCESS);
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      // Refresh stores list
      handleStores();
    }
  }, []);

  const handleStores = () => {
    loader.showLoading(true);
    marketPlaceApiService
      .getStoresList()
      .then(res => {
        loader.showLoading(false);
        if (res.status.code === 200) {
          setStores(res.data);
          const shopifyStores = res.data.shopifyStore || [];
          // Check if there are stores AND at least one has an access token
          const hasShopifyStores = shopifyStores.length > 0;
          const hasAccessToken = shopifyStores.some(store => store.shopifySecretKey && store.shopifySecretKey.trim() !== '');
          // Only show connected if stores exist AND at least one has access token
          setIsShopifyConnected(hasShopifyStores && hasAccessToken);
          setIsAmazonConnected(!!res.data.amazonStore?.length);
          // Show table by default if Shopify is connected (has access token)
          if (hasShopifyStores && hasAccessToken) {
            setShopifyTableVisible(true);
          }
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch(err => {
        loader.showLoading(false);
        if (err.status.code === 500) {
          message.showToast('Invalid credentials', ToastTypes.ERROR);
        } else {
          message.showToast(err?.message || 'Server error. Try again later', ToastTypes.ERROR);
        }
      });
  };

  const handleShopifyClick = () => {
    // Check if stores exist and have access token
    const shopifyStores = stores?.shopifyStore || [];
    const hasAccessToken = shopifyStores.some(store => store.shopifySecretKey && store.shopifySecretKey.trim() !== '');
    
    // If not connected (no stores or no access token), open form
    if (!isShopifyConnected || !hasAccessToken) {
      setShopifyModal(true);
    } else {
      // If connected, toggle table visibility
      setShopifyTableVisible(!shopifyTableVisible);
    }
  };

  // Toggle visibility of sensitive fields
  const toggleFieldVisibility = (storeIndex, fieldName) => {
    const key = `${storeIndex}_${fieldName}`;
    setVisibleFields(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Check if a field is visible
  const isFieldVisible = (storeIndex, fieldName) => {
    const key = `${storeIndex}_${fieldName}`;
    return visibleFields[key] || false;
  };

  // Mask sensitive value
  const maskValue = (value) => {
    if (!value || value === 'N/A') return value;
    return '••••••••••••••••';
  };

  const handleAmazonClick = () => {
    if (!isAmazonConnected) {
      setAmazonModal(true);
      // reset the video whenever modal opens
      setEmbedUrl(`https://www.youtube.com/embed/${videoId}`);
    } else {
      setAmazonTableVisible(!amazonTableVisible);
    }
  };

  const handleShopifyModalClose = () => {
    setShopifyModal(false);
  };

  const handleDisconnectClick = store => {
    setStoreToDisconnect(store);
    setDisconnectModal(true);
  };

  const handleDisconnectConfirm = () => {
    if (!storeToDisconnect) return;

    loader.showLoading(true);
    marketPlaceApiService
      .disconnectShopify(JSON.stringify({ storeDomain: storeToDisconnect.storeDomain }))
      .then(res => {
        loader.showLoading(false);
        if (res.status.code === 200) {
          message.showToast(res.status.message || 'Shopify store disconnected successfully', ToastTypes.SUCCESS);
          setDisconnectModal(false);
          setStoreToDisconnect(null);
          handleStores(); // Refresh the stores list
        } else {
          message.showToast(res.error.message || 'Failed to disconnect Shopify store', ToastTypes.ERROR);
        }
      })
      .catch(err => {
        loader.showLoading(false);
        message.showToast(err?.message || 'Server error. Try again later', ToastTypes.ERROR);
      });
  };

  const handleDisconnectCancel = () => {
    setDisconnectModal(false);
    setStoreToDisconnect(null);
  };

  const handleAmazonModalClose = () => {
    setAmazonModal(false);
    // reset video so it stops playing when modal closes
    setEmbedUrl(`https://www.youtube.com/embed/${videoId}`);
  };

  const handleConnectAmazon = () => {
    setAmazonModal(false);
    // also reset video on navigation
    setEmbedUrl(`https://www.youtube.com/embed/${videoId}`);
    navigate('/amazon/connect');
  };

  // ShopifyForm-like click-to-autoplay
  const openVideoInSection = () => {
    const baseUrl = `https://www.youtube.com/embed/${videoId}`;
    setEmbedUrl(`${baseUrl}?autoplay=1`);
  };

  return (
    <div className="custom_marketplace_bg">
      <div className="marketplace-header-container">
        <h1 className="marketplace-main-title">Connect Your Store</h1>
        <p className="marketplace-subtitle">Select the store you want to connect</p>
      </div>

      <div className="marketplace-logos-container">
        <div className="marketplace-logo-row">
          <div className="marketplace-logo-item">
            <div className="logo-image-wrapper" onClick={handleShopifyClick} style={{ cursor: 'pointer' }}>
              <img src={ShopifyImage} alt="Shopify Logo" className="marketplace-logo" />
            </div>
            {isShopifyConnected ? (
              <div className="connected-label-bottom">
                <GreenTick />
                <span>Connected</span>
              </div>
            ) : (
              <div className="connect-prompt" onClick={handleShopifyClick} style={{ cursor: 'pointer' }}>
                <span>Connect now</span>
              </div>
            )}
          </div>

          <div className="marketplace-logo-item">
            <div className="logo-image-wrapper" onClick={handleAmazonClick} style={{ cursor: 'pointer' }}>
              <img src={AmazonImage} alt="Amazon Logo" className="marketplace-logo" />
            </div>
            {isAmazonConnected ? (
              <div className="connected-label-bottom">
                <GreenTick />
                <span>Connected</span>
              </div>
            ) : (
              <div className="connect-prompt" onClick={handleAmazonClick} style={{ cursor: 'pointer' }}>
                <span>Connect now</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {shopifyModal && <ShopifyForm modalIsOpen={shopifyModal} setIsOpen={setShopifyModal} handleStores={handleStores} />}

      {/* Amazon Modal */}
      {amazonModal && (
        <div className="modal-overlay" onClick={handleAmazonModalClose}>
          <div className="modal-content amazon-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-body">
              <div className="amazon-modal-content-wrapper">
                <div className="store-details-section">
                  <h2 className="store-details-title">Connect Amazon Store</h2>
                  <div className="custom_seller_table_wrapper">
                    <div className="store-details-column">
                      <div className="detail-item">
                        <span className="detail-label">Selling Partner ID:</span>
                        <div className="detail-value">
                          {stores?.amazonStore?.length > 0 ? stores.amazonStore[0]?.sellingPartnerId || 'Not Available' : 'Not Available'}
                        </div>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">SP API OAuth Code:</span>
                        <div className="detail-value">
                          {stores?.amazonStore?.length > 0 ? stores.amazonStore[0]?.spapi_oauth_code || 'Not Available' : 'Not Available'}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="store-details-actions">
                    <button className="connect-button" onClick={handleConnectAmazon}>
                      Connect now
                    </button>
                  </div>
                </div>

                <div className="video-container">
                  <h3 className="video-title">How to Connect Amazon Store</h3>

                  {/* ShopifyForm-like inline iframe, click-to-autoplay */}
                  <div className="video-preview" onClick={openVideoInSection}>
                    <iframe
                      src={embedUrl}
                      title="How to Connect Amazon Store"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      className="amazon_video_iframe"
                      referrerPolicy="strict-origin-when-cross-origin"
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isShopifyConnected && shopifyTableVisible && (
        <div className="stores-section">
          <p className="custom_stores_table_head">Shopify Connected Stores</p>
          <div className="custom_seller_table_wrapper">
            <div className="table-scroll">
              <table className="custom_seller_table">
                <thead>
                  <tr>
                    <td>Store Domain</td>
                    <td>Client ID</td>
                    <td>Client Secret</td>
                    <td>Access Token</td>
                    <td>Action</td>
                  </tr>
                </thead>
                <tbody>
                  {stores?.shopifyStore?.map((store, index) => (
                    <tr key={index}>
                      <td>{store.storeDomain || 'N/A'}</td>
                      <td> {store.shopifyApiKey || 'N/A'}</td>
                      <td>
                        <div className="sensitive-field-container">
                          <span className="sensitive-field-value">
                            {isFieldVisible(index, 'clientSecret') 
                              ? (store.shopifyApiSecret || 'N/A') 
                              : maskValue(store.shopifyApiSecret)}
                          </span>
                          <span 
                            className="eye-icon" 
                            onClick={() => toggleFieldVisibility(index, 'clientSecret')}
                            title={isFieldVisible(index, 'clientSecret') ? 'Hide' : 'Show'}
                          >
                            {isFieldVisible(index, 'clientSecret') ? <FiEyeOff /> : <FiEye />}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="sensitive-field-container">
                          <span className="sensitive-field-value">
                            {isFieldVisible(index, 'accessToken') 
                              ? (store.shopifySecretKey || 'N/A') 
                              : maskValue(store.shopifySecretKey)}
                          </span>
                          <span 
                            className="eye-icon" 
                            onClick={() => toggleFieldVisibility(index, 'accessToken')}
                            title={isFieldVisible(index, 'accessToken') ? 'Hide' : 'Show'}
                          >
                            {isFieldVisible(index, 'accessToken') ? <FiEyeOff /> : <FiEye />}
                          </span>
                        </div>
                      </td>
                      <td>
                        <button
                          onClick={() => handleDisconnectClick(store)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#ef4444',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'background-color 0.2s',
                          }}
                          onMouseOver={e => (e.target.style.backgroundColor = '#dc2626')}
                          onMouseOut={e => (e.target.style.backgroundColor = '#ef4444')}
                        >
                          Disconnect
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <DeletePopUp
        open={disconnectModal}
        handleClose={handleDisconnectCancel}
        heading="Disconnect Shopify Store"
        text={`Are you sure you want to disconnect ${storeToDisconnect?.storeDomain}? This action cannot be undone.`}
        submitText="Disconnect"
        cancelText="Cancel"
        handleSubmit={handleDisconnectConfirm}
        handleCancel={handleDisconnectCancel}
      />

      {isAmazonConnected && amazonTableVisible && (
        <>
          <p className="custom_stores_table_head">Amazon Connected Store</p>
          <div className="custom_seller_table_wrapper">
            <table className="custom_seller_table">
              <thead>
                <tr>
                  <td>Selling Partner Id</td>
                  <td>Sp Api Oauth Code</td>
                </tr>
              </thead>
              <tbody>
                {stores?.amazonStore?.map((store, index) => (
                  <tr key={index}>
                    <td>{store.sellingPartnerId}</td>
                    <td>{store.spapi_oauth_code}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
